/* eslint-disable camelcase */

import React from 'react';
import { connect } from 'react-redux';
import R from 'ramda';
import PropTypes from 'prop-types';
import { HeroFullOverlay, HeroFullBlock } from '@cotyorg/ccx-organism-hero';
import Heading from '@cotyorg/ccx-atoms/Heading';
import Paragraph from '@cotyorg/ccx-atoms/Paragraph';
import { VideoPlayer } from '@cotyorg/ccx-organism-video';
import {
  trackVideoPlayEvent,
  trackVideoMilestoneEvent,
} from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/video';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import StandardLayout from '../../templates/StandardLayout';
import { isLargeBreakpoint } from '../../utils';

const AboutComponent = ({
  currentBreakpoint,
  noOfHeroSection1Heroes,
  sectionHeadings,
  textSections,
  videos,
}) => (
  <StandardLayout>
    <div className="about-page">
      <div className="grid-container">
        <div className="grid-x grid-margin-x">
          <div className="cell">
            <Heading extraClass="h2 about-page__main-heading" level={1}>
              {R.path(['mainHeading', 'heading'])(sectionHeadings)}
            </Heading>
          </div>
          <div className="cell">
            <OptionalSection
              renderCondition={!R.isEmpty(R.path(['mainVideo', 'url'], videos))}
              section={() => (
                <div className="video-container">
                  <VideoPlayer
                    videosStoreDataKey="mainVideo"
                    showControls={false}
                    onProgress={({ url, duration, progress, videoMilestone }) =>
                      trackVideoMilestoneEvent({
                        url,
                        duration,
                        progress,
                        videoMilestone,
                        title: R.path(['mainVideo', 'name'], videos),
                      })
                    }
                    onPlay={({ url, duration, progress }) =>
                      trackVideoPlayEvent({
                        url,
                        duration,
                        progress,
                        title: R.path(['mainVideo', 'name'], videos),
                      })
                    }
                    config={{
                      youtube: {
                        playerVars: {
                          controls: 1,
                        },
                        embedOptions: {
                          host: 'https://www.youtube-nocookie.com',
                        },
                      },
                    }}
                  />
                </div>
              )}
            />
          </div>
          <div className="cell xsmall-14 xsmall-offset-1">
            {[...Array(noOfHeroSection1Heroes)].map((item, index) => {
              const isContentFirst =
                index % 2 !== 0 && isLargeBreakpoint({ currentBreakpoint });

              return (
                <HeroFullBlock
                  heroStoreDataKey={`heroSection1Hero${index + 1}`}
                  isContentFirst={isContentFirst}
                  isUnderlined={false}
                />
              );
            })}
          </div>

          {textSections.map(
            ({ heading, description, rich_description, hero }, index) => (
              <>
                {hero.length > 0 && (
                  <div className="cell">
                    <HeroFullOverlay
                      isContentFirst={isLargeBreakpoint({ currentBreakpoint })}
                      heroStoreDataKey={`heroTextSectionHero${index + 1}`}
                    />
                  </div>
                )}
                <div className="cell xsmall-14 xsmall-offset-1 large-9">
                  <Heading level={2}>{heading}</Heading>
                  {description && <Paragraph>{description}</Paragraph>}
                  {rich_description && (
                    // eslint-disable-next-line react/no-danger
                    <div
                      dangerouslySetInnerHTML={{ __html: rich_description }}
                    />
                  )}
                </div>
              </>
            )
          )}
          <div className="cell">
            <OptionalSection
              renderCondition={
                !R.isEmpty(R.path(['bottomVideo', 'url'], videos))
              }
              section={() => (
                <div className="video-container">
                  <VideoPlayer
                    videosStoreDataKey="bottomVideo"
                    showControls={false}
                    onProgress={({ url, duration, progress, videoMilestone }) =>
                      trackVideoMilestoneEvent({
                        url,
                        duration,
                        progress,
                        videoMilestone,
                        title: '',
                      })
                    }
                    onPlay={({ url, duration, progress }) =>
                      trackVideoPlayEvent({
                        url,
                        duration,
                        progress,
                        title: '',
                      })
                    }
                    config={{
                      youtube: {
                        playerVars: {
                          controls: 1,
                        },
                        embedOptions: {
                          host: 'https://www.youtube-nocookie.com',
                        },
                      },
                    }}
                  />
                </div>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  </StandardLayout>
);

AboutComponent.propTypes = {
  currentBreakpoint: PropTypes.string,
  noOfHeroSection1Heroes: PropTypes.number,
  sectionHeadings: PropTypes.objectOf(PropTypes.object),
  textSections: PropTypes.arrayOf(PropTypes.object),
  videos: PropTypes.objectOf(PropTypes.object),
};

export default connect(({ content, breakpoints }) => ({
  currentBreakpoint: R.prop('current', breakpoints),
  noOfHeroSection1Heroes: R.path(['custom', 'noOfHeroSection1Heroes'], content),
  sectionHeadings: R.prop('sectionHeadings', content),
  textSections: R.prop('textSections', content),
  videos: R.prop('videos', content),
}))(AboutComponent);
